/**
 * Archive, Delete, and — on a thread already through — Block.
 *
 * WHY ARCHIVE AND DELETE ARE NOT ON THE KNOCK CARD, beside the verbs. Those say
 * something about the PERSON — you are through, you are not for me, you are
 * blocked — and ruling R-0828-1 is explicit that these two are not decisions
 * about the sender at all: they are the reader tidying their own list, they
 * reach nobody, and they change no standing. Giving them the same pills would
 * put "put this away" at the same weight as "turn this person away", and
 * somebody would eventually press the wrong one.
 *
 * WHY THE HEADER. The card is the incoming surface only, and its verbs
 * disappear once a knock is through (R-0826-4). Archive has to work on all of
 * it — incoming, outgoing, answered, declined — so it lives on the one piece of
 * chrome every conversation has, behind the quietest control on it. The sort
 * menu two panes over is the same shape, on purpose.
 *
 * AND THAT IS WHY BLOCK IS HERE TOO (`docs/conversation-model.md` §3). A person
 * let in can still be shut out — `let_in` → `blocked` is a transition the gate
 * table allows — but the card drops its four verbs the moment the door opens,
 * because under a chat with somebody already inside they are the wrong four
 * things to offer. That left the one verb that still applies with nowhere to
 * be. It is here, at the quiet end of the menu, on an incoming thread that is
 * through: the same action, the same confirm, and the same thirty-second undo
 * the verb has at the door, because it IS that verb — the shell's one copy of
 * it, handed down (`shell/desk.ts`), so there can never be two undo windows on
 * one knock.
 *
 * DELETE ASKS FIRST, AND THE QUESTION STATES THE CONSEQUENCE. It is the only
 * irreversible thing in the console short of taking a door down, and the
 * sentence people need is not "are you sure" but "whose copy": theirs stays.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  deleteConversationCopy,
  setConversationArchived,
  type ConversationMarkResult,
} from "../../lib/api";
import { knockRefOf, sidesOf, type Conversation } from "../../lib/conversations";
import { qk } from "../../lib/queries";
import { useSessionExpired } from "../../lib/session";
import { Button, LinkButton } from "../ui/button";
import { MoreIcon } from "../ui/icons";
import { BLOCK_ASK, BLOCK_NOTE } from "../inbox/knock-card";
import { Sheet } from "./surfaces";
import { useShellUi } from "./state";

export const MENU_LABEL = "More for this conversation";
export const ARCHIVE_LABEL = "Archive";
export const UNARCHIVE_LABEL = "Put back in your lists";
export const DELETE_LABEL = "Delete my copy";

export const DELETE_QUESTION = "Delete your copy?";
export const DELETE_NOTE =
  "This removes the whole conversation — both directions of it — from your lists for good, " +
  "Archived included. There is no undo. The other side keeps theirs, and your plan's own " +
  "record of the work is untouched.";
export const DELETE_CONFIRM_LABEL = "Delete my copy";
export const DELETE_KEEP_LABEL = "Keep it";
export const MARK_FAILED = "Nob is not answering. Try that again.";

/**
 * BLOCK, ON A THREAD ALREADY THROUGH (§3). The label, the question and the
 * consequence are the card's own — imported rather than retyped, so the two
 * places somebody can shut a person out cannot come to describe it differently.
 */
export const BLOCK_LABEL = "Block";
/**
 * The card's cancel reads "Keep at the door", which is true where it is asked
 * and false here: this person is not at the door, they are inside. The question
 * and its consequence are shared; the way out of it is the one sentence that
 * has to differ.
 */
export const BLOCK_KEEP_LABEL = "Keep them";
export const UNDO_LABEL = "Undo";

/**
 * Both writes move the same two lists — the door's inbox and the reader's own
 * sent knocks — because a conversation can be either, and the menu does not
 * branch on which. Two invalidations for one act is cheaper than a menu that
 * has to know what it is looking at.
 */
function useReread(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["inbox"] });
    void queryClient.invalidateQueries({ queryKey: qk.myKnocks() });
    void queryClient.invalidateQueries({ queryKey: qk.threads() });
    void queryClient.invalidateQueries({ queryKey: qk.recap() });
  }, [queryClient]);
}

/**
 * The block verb, handed down rather than re-implemented.
 *
 * It is the shell's ONE copy (`shell/desk.ts` → `useInboxActions`), so the
 * write, the thirty-second window and the list invalidation that fires when it
 * lapses all happen exactly once however the owner reached the verb. The menu
 * owns only the question and the place the Undo stands, because the card that
 * usually holds both is folded away on a through thread.
 *
 * Absent when there is nothing to block: the row is outgoing, it is not through
 * yet, or the pane has not resolved a knock at this reader's own door.
 */
export type BlockControls = {
  /** The block landed and is still reversible. */
  undoable: boolean;
  /** The dry confirm the action left behind — "Blocked.", or a refusal. */
  note: string | null;
  busy: boolean;
  onConfirm: () => void;
  onUndo: () => void;
};

export function ConversationMenu({
  conv,
  block,
}: {
  conv: Conversation;
  block?: BlockControls | undefined;
}) {
  const ui = useShellUi();
  const expireSession = useSessionExpired();
  const reread = useReread();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);

  const ref = knockRefOf(conv);

  useEffect(() => {
    if (!open) return;
    const shut = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("click", shut);
    return () => window.removeEventListener("click", shut);
  }, [open]);

  // A conversation switch must not carry a half-open menu, or an unanswered
  // question about the conversation somebody just left, into the next one.
  useEffect(() => {
    setOpen(false);
    setAsking(false);
    setBlocking(false);
    setError(null);
  }, [conv.id]);

  const settle = useCallback(
    (r: ConversationMarkResult): boolean => {
      if (r.ok) {
        setError(null);
        reread();
        return true;
      }
      if (r.code === "UNAUTHENTICATED") {
        expireSession();
        return false;
      }
      setError(MARK_FAILED);
      return false;
    },
    [expireSession, reread],
  );

  if (!ref) return null;

  /**
   * WHO CAN STILL BE SHUT OUT (§3's gate table). `let_in` → `blocked` is a
   * transition, and `connected` is two `let_in` rows, so both offer it. An
   * OUTGOING row never does: blocking is a thing a door's owner does to somebody
   * who knocked at it, and the reader is the knocker on that side.
   *
   * At the door the card still carries the verb, so the menu does not repeat it
   * — one place to press per state, and no way to open two confirms at once.
   */
  const throughRow = ref.side === "in" && (conv.stage === "through" || conv.connected);
  const canBlock = block !== undefined && throughRow && !block.undoable;

  /**
   * BOTH SIDES, ON THIS SIDE OF THE DOOR (§6.3). A thread is one conversation
   * and putting it away puts all of it away; the server's marks stay per knock,
   * so the menu writes one per ROW — at most two, one per direction, and both
   * are already the reader's own copy, because that is the only copy the
   * threads read returns. The first refusal stops the run: a half-archived
   * thread would come back as a row whose face says one thing and whose menu
   * says another.
   */
  const eachSide = async (
    write: (knockId: string) => Promise<ConversationMarkResult>,
  ): Promise<boolean> => {
    for (const side of sidesOf(conv)) {
      if (!settle(await write(side.knockId))) return false;
    }
    return true;
  };

  const archive = async (archived: boolean) => {
    setBusy(true);
    const done = await eachSide((knock_id) => setConversationArchived({ knock_id, archived }));
    setBusy(false);
    if (done) setOpen(false);
  };

  const remove = async () => {
    setBusy(true);
    const done = await eachSide((knock_id) => deleteConversationCopy({ knock_id }));
    setBusy(false);
    if (!done) return;
    setAsking(false);
    // The row this pane was drawing is gone. Landing back on the empty state is
    // the honest thing to do — leaving the pane on a deleted conversation would
    // be the product still holding the copy it just said it had removed.
    ui.setActive(null);
    ui.setMobilePane("list");
  };

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-label={MENU_LABEL}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="nob-press grid size-9 place-items-center rounded-full text-muted
                   hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-focus/70"
      >
        <MoreIcon className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={MENU_LABEL}
          className="absolute top-10 right-0 z-20 w-56 rounded-md border border-border bg-bg py-1
                     shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => void archive(!conv.archived)}
            className="block w-full px-3 py-1.5 text-left text-sm text-muted transition-colors
                       duration-75 hover:bg-hover hover:text-text focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            {conv.archived ? UNARCHIVE_LABEL : ARCHIVE_LABEL}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => {
              setOpen(false);
              setAsking(true);
            }}
            className="block w-full px-3 py-1.5 text-left text-sm text-danger transition-colors
                       duration-75 hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                       focus-visible:ring-focus/70"
          >
            {DELETE_LABEL}
          </button>
          {/* Last, and in the danger colour Delete wears: it is the one item
              here that reaches the other person. */}
          {canBlock && (
            <button
              type="button"
              role="menuitem"
              disabled={busy || block.busy}
              onClick={() => {
                setOpen(false);
                setBlocking(true);
              }}
              className="block w-full px-3 py-1.5 text-left text-sm text-danger transition-colors
                         duration-75 hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-focus/70"
            >
              {BLOCK_LABEL}
            </button>
          )}
        </div>
      )}
      {error && !asking && (
        <p className="absolute top-10 right-0 z-20 w-56 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
      {/* THE UNDO STANDS WHERE THE VERB WAS PRESSED (FR-6.4), AND ONLY THERE.
          At the door the card holds it, in full view; on a through thread the
          card is folded away under the conversation, so the promise is kept
          beside the control that made it. Gated on the same row this menu
          offers the verb on, or an owner blocking from the card would be
          promised the same undo twice. */}
      {throughRow && block?.undoable && (
        <p
          data-block-undo
          className="absolute top-10 right-0 z-20 flex w-56 items-baseline justify-end gap-2
                     text-sm text-muted"
          role="status"
        >
          <span>{block.note}</span>
          <LinkButton type="button" disabled={block.busy} onClick={block.onUndo}>
            {UNDO_LABEL}
          </LinkButton>
        </p>
      )}
      {asking && (
        <Sheet title={DELETE_QUESTION} lede={DELETE_NOTE} onClose={() => setAsking(false)}>
          {error && (
            <p className="mb-3 text-sm text-danger" role="alert">
              {error}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={busy}
              onClick={() => void remove()}
              className="h-10 px-4"
            >
              {DELETE_CONFIRM_LABEL}
            </Button>
            <LinkButton type="button" disabled={busy} onClick={() => setAsking(false)}>
              {DELETE_KEEP_LABEL}
            </LinkButton>
          </div>
        </Sheet>
      )}
      {/* THE SAME QUESTION THE CARD ASKS, in the card's own words: the row
          leaves the lists, they cannot knock here again, and nothing is
          deleted. The verb behind the confirm is the shell's one copy, so the
          undo it comes with is the same undo. */}
      {blocking && block && (
        <Sheet title={BLOCK_ASK} lede={BLOCK_NOTE} onClose={() => setBlocking(false)}>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={block.busy}
              onClick={() => {
                setBlocking(false);
                block.onConfirm();
              }}
              className="h-10 px-4"
            >
              {BLOCK_LABEL}
            </Button>
            <LinkButton type="button" disabled={block.busy} onClick={() => setBlocking(false)}>
              {BLOCK_KEEP_LABEL}
            </LinkButton>
          </div>
        </Sheet>
      )}
    </div>
  );
}
